// Page-wide UI state shared between the filter bar, table headers, pagination,
// and the slide panel (category manager / content form). Server data lives in
// React Query — this store only tracks transient screen state per PRD 10 §5.
//
// Slice 1 establishes the shell + panel targets. The list query (Slice 2),
// filter/search/sort (Slice 3), category manager (Slice 4), and content form
// (Slice 5+) all read/drive this store.

import { create } from 'zustand';

import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';
import type {
  CategoryFilter,
  ContentSortKey,
  SortDir,
} from '../types/health-encyclopedia.types';

// PRD §3.4 / §3.5 — slide-panel target. `null` is closed.
//   - { mode: 'category-manager' } opens the category management panel.
//   - { mode: 'create' } opens an empty content form.
//   - { mode: 'edit', eid } opens the form hydrated with that content.
export type PanelTarget =
  | null
  | { mode: 'category-manager' }
  | { mode: 'create' }
  | { mode: 'edit'; eid: string };

interface HealthEncyclopediaPageState {
  // null = "전체 카테고리" (all); a number selects that category. (미분류 removed
  // from scope — ADM.010.)
  selectedCategoryId: CategoryFilter | null;
  search: string;
  sortKey: ContentSortKey;
  sortDir: SortDir;
  page: number;
  perPage: number;
  panel: PanelTarget;

  // Category/search changes reset to page 1 so the user sees the top of the new
  // result set (matches prototype). Sort also resets to page 1 (the prototype
  // does this on header click, since reordering makes the current page moot).
  setCategory: (id: CategoryFilter | null) => void;
  setSearch: (q: string) => void;
  toggleSort: (key: ContentSortKey) => void;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
  openCreate: () => void;
  openEdit: (eid: string) => void;
  openCategoryManager: () => void;
  closePanel: () => void;
  reset: () => void;
}

// PRD §4.2 — default sort is EID descending (newest first), first page, all
// categories, panel closed.
const INITIAL = {
  selectedCategoryId: null,
  search: '',
  sortKey: 'eid' as const,
  sortDir: 'desc' as const,
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  panel: null,
};

export const useHealthEncyclopediaPageStore = create<HealthEncyclopediaPageState>((set) => ({
  ...INITIAL,
  setCategory: (id) => set({ selectedCategoryId: id, page: 1 }),
  setSearch: (q) => set({ search: q, page: 1 }),
  toggleSort: (key) =>
    set((s) =>
      s.sortKey === key
        ? { sortDir: s.sortDir === 'asc' ? 'desc' : 'asc', page: 1 }
        : { sortKey: key, sortDir: 'asc', page: 1 },
    ),
  setPage: (p) => set({ page: Math.max(1, p) }),
  setPerPage: (n) => set({ perPage: Math.max(1, n), page: 1 }),
  openCreate: () => set({ panel: { mode: 'create' } }),
  openEdit: (eid) => set({ panel: { mode: 'edit', eid } }),
  openCategoryManager: () => set({ panel: { mode: 'category-manager' } }),
  closePanel: () => set({ panel: null }),
  reset: () => set(INITIAL),
}));
